import React, { useEffect, useMemo, useRef, useState } from "react";

import { Box, IconButton, Skeleton, Stack, Typography } from "@mui/material";
import { Notifications, LocationOn, Comment, Favorite, PhotoLibrary } from "@mui/icons-material";

import {
	Template01,
	Template02,
	Template03,
	Template04,
	Template05,
	Template06,
	Template07,
	Template08
} from "../../../Components/Templates/WeddingTemplates";
import {
	IGallery,
	INVITATION_ENTRANCE,
	INVITATION_PRICING_PLANS_ENUM,
	INVITATION_STATUS,
	INVITATION_TYPES,
	Invitations,
	TEMPLATES
} from "../../../store/services/invitations/dto/invitations.dto";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ButtonComponent from "../../../Components/ButtonComponent";
import { WeddingInvitationContainer } from "./styles";
import {
	useCreatInvitationFromTemplateMutation,
	useLazyGetWeddingInvitationCommentQuery,
	useLazyGetWeddingInvitationCommentsQuery,
	useLazyGetWeddingInvitationGalleryQuery,
	useLazyGetWeddingInvitationQuery,
	useLazyValidateWeddingInvitationQuery,
	usePostViewMutation
} from "../../../store/services/invitations/invitations.service";
import { useAppDispatch, useAppSelector } from "../../../store";
import { getFullState } from "../../../store/slices/auth/auth.slice";
import InvitationLoader from "../../../Components/InvtationLoader";
import NoDataFallback from "../../../Components/NoDataFallback";
import LockedInvitation from "../../../Components/LockedInvitation";
import { IError } from "../../../utils/getErrorMessage";
import {
	getFullInvitationsState,
	resetInvitation,
	toggleImageGallerySlides
} from "../../../store/slices/invitations/invitations.slice";
import { notifyError } from "../../../utils/NotifyError";
import { getOrCreateAnonId } from "../../../utils/getOrCreateAnonId";
import ImageSlideShow from "../../../Components/ImageSlideShow";
import { trackEvent } from "../../../utils/analytics";
import RsvpModal from "../../../Components/Modals/RSVP";
import { LANG_AR, LANG_EN, RTL } from "../../../Components/Templates/WeddingTemplates/constants";
import langEN from "../../../Components/Templates/WeddingTemplates/lang.EN.json";
import langAR from "../../../Components/Templates/WeddingTemplates/lang.AR.json";
import { trackMetaEvent } from "../../../utils/metaPixel";
import { trackTikTokEvent } from "../../../utils/tiktokPixel";
import NotificationsConfig from "../../../Components/Modals/NotificationsConfig";
import {
	CurtainEntrance,
	EnvelopeEntrance,
	NamesEntrance,
	PalaceDoorsEntrance,
	RingsEntrance
} from "./InvitationEntrances";
import { getTimeZone } from "../../../utils/getTimeZone";
import { InvitationColorMatrix, TemplateColorObject } from "../../../InvitationColorMatrix";
import InvitationColorPicker from "../../../Components/InvitationColorPicker";
import InvitationEntrancePicker from "../../../Components/InvitationEntrancePicker";
import UploadMemories from "../../../Components/Modals/UploadMemories";
import { addDays, isAfter } from "date-fns";
import ConfirmModal from "../../../Components/Modals/ConfirmModal";
import WeddingMeta from "../../../Components/WeddingMeta";

type TemplateRef = {
	scrollToComments: () => void;
	scrollToLocation: () => void;
};

const WeddingInvitationTemplates = () => {
	const [rsvpReminder, setRSVPReminder] = useState(false);
	const [upload, setUpload] = useState(false);
	const [openEntrance, setOpenEntrance] = useState(false);
	const [selectedColorMatrix, setSelectedColorMatrix] = useState(1);
	const [selectedTemplateEntrance, setSelectedTemplateEntrance] = useState<INVITATION_ENTRANCE | null>(null);
	const invitationWrapperRef = useRef<HTMLElement>(null);
	const templateRef = useRef<TemplateRef>(null);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
	const dispatch = useAppDispatch();
	const { selectedInvitation, selectedInvitationGallery, showImageGallerySlides, myComment, initialSlideShowImage } =
		useAppSelector(getFullInvitationsState);
	const params = useParams();
	const id = params.id;
	const { isLoggedIn } = useAppSelector(getFullState);
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const password = searchParams.get("password");
	const query_anonymous_token = searchParams.get("anonymous_token");
	const [showCommentModal, setShowCommentModal] = useState(false);
	const [showNotificationSubModal, setShowNotificationSubModal] = useState(false);
	const [fetchInvitation, { isLoading: isFetchingInvitation }] = useLazyGetWeddingInvitationQuery();
	const [fetchGallery] = useLazyGetWeddingInvitationGalleryQuery();
	const [fetchComments] = useLazyGetWeddingInvitationCommentsQuery();
	const [fetchMyComment] = useLazyGetWeddingInvitationCommentQuery();
	const lang = selectedInvitation?.language === LANG_EN ? langEN : langAR;
	const hasShownRef = useRef(false);
	const timeReachedRef = useRef(false);
	const [isInvitationLoaded, setIsInvitationLoaded] = useState(false);

	const [callValidateInvitation, { isLoading: isValidatingInvitation, error: validationError }] =
		useLazyValidateWeddingInvitationQuery();
	const [callPageView] = usePostViewMutation();
	const [callCreateInvitationFromTemplate, { isLoading: isCreatingTemplate }] =
		useCreatInvitationFromTemplateMutation();

	useEffect(() => {
		const body = {
			id: id || "",
			password: password || ""
		};
		callValidateInvitation(body).then(result => {
			const anonymous_token = query_anonymous_token ? query_anonymous_token : getOrCreateAnonId(id || "");
			if (result.data?.canAccess) {
				fetchInvitation(body);
				callPageView({ _id: id as string, deviceId: anonymous_token, password: password as string });
				fetchGallery(body);
				if (!result.data.isTemplate) {
					fetchComments(body);
					fetchMyComment({ ...body, anonymous_token });
				}
			}
		});
	}, [id, password]);

	useEffect(() => {
		if (
			myComment ||
			selectedInvitation?.type === INVITATION_TYPES.TEMPLATE ||
			selectedInvitation?.selectedPricingPlan === INVITATION_PRICING_PLANS_ENUM.FREE
		) {
			return;
		}

		const timer = setTimeout(() => {
			timeReachedRef.current = true;
		}, 10000);

		const handleScroll = () => {
			if (hasShownRef.current || !timeReachedRef.current) {
				return;
			}

			const scrollTop = window.scrollY;

			const documentHeight = document.documentElement.scrollHeight - window.innerHeight;

			const scrollPercent = (scrollTop / documentHeight) * 100;

			if (scrollPercent >= 40) {
				hasShownRef.current = true;
				setRSVPReminder(true);
			}
		};

		window.addEventListener("scroll", handleScroll);

		return () => {
			clearTimeout(timer);

			window.removeEventListener("scroll", handleScroll);
		};
	}, [myComment, selectedInvitation]);

	useEffect(() => {
		if (!openEntrance || !selectedInvitation) return;
		const invitationWrapper: HTMLElement = document.getElementById("template_wrapper") as HTMLElement;
		if (!selectedInvitation.has_images) {
			invitationWrapper.style.opacity = "1";
			return setIsInvitationLoaded(true);
		} else {
			if (invitationWrapper) {
				invitationWrapper.style.opacity = "0";
			}
		}
		const image: HTMLImageElement = document.getElementById("main_image") as HTMLImageElement;
		if (!image) return;
		const handleImageLoaded = () => {
			setIsInvitationLoaded(true);

			if (!invitationWrapper) return;
			invitationWrapper.style.opacity = "1";
		};
		image.addEventListener("load", handleImageLoaded);
		if (image.complete) {
			handleImageLoaded();
		}
	}, [selectedInvitation, openEntrance]);

	useEffect(() => {
		if (!openEntrance || !selectedInvitation) return;
		if (!containerRef.current) return;

		const elements = containerRef.current.querySelectorAll(".animate-on-scroll");

		const observer = new IntersectionObserver(
			entries => {
				entries.forEach(entry => {
					if (entry.isIntersecting) {
						entry.target.classList.add("visible");
						observer.unobserve(entry.target);
					}
				});
			},
			{
				threshold: 0.2
			}
		);

		elements.forEach(element => {
			observer.observe(element);
		});

		return () => {
			observer.disconnect();
		};
	}, [selectedInvitation, openEntrance]);

	const invitation = useMemo(
		() => ({
			title:
				selectedInvitation?.language === LANG_EN
					? `${selectedInvitation?.groom_name} & ${selectedInvitation?.bride_name}'s Wedding 💍`
					: ` حفل زِفاف  ${selectedInvitation?.groom_name} و ${selectedInvitation?.bride_name} 💍`,
			description: selectedInvitation?.intro_text
				? selectedInvitation?.intro_text
				: "Join us on our special day and celebrate love with us.",
			image: `${selectedInvitation?.main_image}`,
			url: `${process.env.REACT_APP_SHARE}/weddingInvitation/${selectedInvitation?._id}`
		}),
		[selectedInvitation]
	);

	useEffect(() => {
		return () => {
			dispatch(resetInvitation());
			setSelectedTemplateId(null);
			setOpenEntrance(false);
		};
	}, []);

	useEffect(() => {
		if (!openEntrance) return;

		const timeout = setTimeout(() => {
			const el = invitationWrapperRef.current;
			if (el) {
				el.style.overflow = "unset";
				el.style.height = "unset";
			}
		}, 2000);

		return () => clearTimeout(timeout);
	}, [openEntrance]);

	useEffect(() => {
		if (selectedInvitation) {
			setSelectedTemplateId(selectedInvitation.template_id);
		}
	}, [selectedInvitation]);

	const handeScrollToComments = () => {
		templateRef.current && templateRef.current.scrollToComments();
	};
	const handeScrollToLocation = () => {
		templateRef.current && templateRef.current.scrollToLocation();
	};

	const templates: React.ReactElement = useMemo(() => {
		if (!selectedTemplateId) {
			return <InvitationLoader />;
		}
		const invitationData = selectedInvitation as Invitations;
		if (!invitationData) {
			return <InvitationLoader />;
		}
		// const color_id = invitationData ? invitationData.template_color : 1;
		const _list: Record<number, React.ReactElement> = {
			[TEMPLATES.TEMPLATES01]: (
				<Template01
					data={invitationData}
					gallery={selectedInvitationGallery as IGallery[]}
					colorMatrix={
						InvitationColorMatrix[TEMPLATES.TEMPLATES01][
							invitationData?.type === INVITATION_TYPES.TEMPLATE ? selectedColorMatrix : invitationData?.template_color
						]
					}
					ref={templateRef}
				/>
			),
			[TEMPLATES.TEMPLATES02]: (
				<Template02
					data={invitationData}
					gallery={selectedInvitationGallery as IGallery[]}
					colorMatrix={
						InvitationColorMatrix[TEMPLATES.TEMPLATES02][
							invitationData?.type === INVITATION_TYPES.TEMPLATE ? selectedColorMatrix : invitationData?.template_color
						]
					}
					ref={templateRef}
				/>
			),
			[TEMPLATES.TEMPLATES03]: (
				<Template03
					data={invitationData}
					gallery={selectedInvitationGallery as IGallery[]}
					colorMatrix={
						InvitationColorMatrix[TEMPLATES.TEMPLATES03][
							invitationData?.type === INVITATION_TYPES.TEMPLATE ? selectedColorMatrix : invitationData?.template_color
						]
					}
					ref={templateRef}
				/>
			),
			[TEMPLATES.TEMPLATES04]: (
				<Template04
					data={invitationData}
					gallery={selectedInvitationGallery as IGallery[]}
					colorMatrix={
						InvitationColorMatrix[TEMPLATES.TEMPLATES04][
							invitationData?.type === INVITATION_TYPES.TEMPLATE ? selectedColorMatrix : invitationData?.template_color
						]
					}
					ref={templateRef}
				/>
			),
			[TEMPLATES.TEMPLATES05]: (
				<Template05
					data={invitationData}
					gallery={selectedInvitationGallery as IGallery[]}
					colorMatrix={
						InvitationColorMatrix[TEMPLATES.TEMPLATES05][
							invitationData?.type === INVITATION_TYPES.TEMPLATE ? selectedColorMatrix : invitationData?.template_color
						]
					}
					ref={templateRef}
				/>
			),
			[TEMPLATES.TEMPLATES06]: (
				<Template06
					data={invitationData}
					gallery={selectedInvitationGallery as IGallery[]}
					colorMatrix={
						InvitationColorMatrix[TEMPLATES.TEMPLATES06][
							invitationData?.type === INVITATION_TYPES.TEMPLATE ? selectedColorMatrix : invitationData?.template_color
						]
					}
					ref={templateRef}
				/>
			),
			[TEMPLATES.TEMPLATES07]: (
				<Template07
					data={invitationData}
					gallery={selectedInvitationGallery as IGallery[]}
					colorMatrix={
						InvitationColorMatrix[TEMPLATES.TEMPLATES07][
							invitationData?.type === INVITATION_TYPES.TEMPLATE ? selectedColorMatrix : invitationData?.template_color
						]
					}
					ref={templateRef}
				/>
			),
			[TEMPLATES.TEMPLATES08]: (
				<Template08
					data={invitationData}
					gallery={selectedInvitationGallery as IGallery[]}
					colorMatrix={
						InvitationColorMatrix[TEMPLATES.TEMPLATES08][
							invitationData?.type === INVITATION_TYPES.TEMPLATE ? selectedColorMatrix : invitationData?.template_color
						]
					}
					ref={templateRef}
				/>
			)
		};
		return _list[selectedTemplateId];
	}, [selectedTemplateId, selectedInvitation, selectedInvitationGallery, selectedColorMatrix]);

	const InvitationEntrance: React.ReactElement | null = useMemo(() => {
		if (!selectedInvitation || !selectedTemplateId) {
			return null;
		}
		const _entrance = {
			[INVITATION_ENTRANCE.NAMES]: (
				<NamesEntrance
					isOpened={openEntrance}
					setIsOpened={setOpenEntrance}
					selectedInvitation={selectedInvitation}
					colorMatrix={
						InvitationColorMatrix[selectedTemplateId][
							selectedInvitation.type === INVITATION_TYPES.TEMPLATE
								? selectedColorMatrix
								: selectedInvitation.template_color
						]
					}
				/>
			),
			[INVITATION_ENTRANCE.ENVELOPE]: (
				<EnvelopeEntrance
					openEnvelope={openEntrance}
					setOpenEnvelope={setOpenEntrance}
					selectedInvitation={selectedInvitation}
					colorMatrix={
						InvitationColorMatrix[selectedTemplateId][
							selectedInvitation.type === INVITATION_TYPES.TEMPLATE
								? selectedColorMatrix
								: selectedInvitation.template_color
						]
					}
				/>
			),
			[INVITATION_ENTRANCE.RINGS]: (
				<RingsEntrance
					isOpened={openEntrance}
					setIsOpened={setOpenEntrance}
					selectedInvitation={selectedInvitation}
					colorMatrix={
						InvitationColorMatrix[selectedTemplateId][
							selectedInvitation.type === INVITATION_TYPES.TEMPLATE
								? selectedColorMatrix
								: selectedInvitation.template_color
						]
					}
				/>
			),
			[INVITATION_ENTRANCE.CURTAIN]: (
				<CurtainEntrance
					isOpened={openEntrance}
					setIsOpened={setOpenEntrance}
					selectedInvitation={selectedInvitation}
					colorMatrix={
						InvitationColorMatrix[selectedTemplateId][
							selectedInvitation.type === INVITATION_TYPES.TEMPLATE
								? selectedColorMatrix
								: selectedInvitation.template_color
						]
					}
				/>
			),
			[INVITATION_ENTRANCE.PALACE_DOORS]: (
				<PalaceDoorsEntrance
					isOpened={openEntrance}
					setIsOpened={setOpenEntrance}
					selectedInvitation={selectedInvitation}
					colorMatrix={
						InvitationColorMatrix[selectedTemplateId][
							selectedInvitation.type === INVITATION_TYPES.TEMPLATE
								? selectedColorMatrix
								: selectedInvitation.template_color
						]
					}
				/>
			)
		};

		const entrance =
			selectedInvitation.type === INVITATION_TYPES.TEMPLATE && selectedTemplateEntrance !== null
				? selectedTemplateEntrance
				: selectedInvitation.entrance || 0;

		return _entrance[entrance as INVITATION_ENTRANCE];
	}, [selectedInvitation, selectedTemplateId, openEntrance, selectedColorMatrix, selectedTemplateEntrance]);

	const handleUseTemplate = () => {
		if (isLoggedIn && id) {
			trackEvent("add_invitation", {
				scope: "Invitations",
				description: "Add Invitation to user's account"
			});
			const timezone = getTimeZone();
			callCreateInvitationFromTemplate({ template_id: id, timezone, template_color: 1 }).then(({ data, error }) => {
				if (error) {
					return notifyError(error as IError);
				}
				trackMetaEvent(
					"use_template",
					{ template_id: id, invitation_id: data?.invitation_id, currency: "EGP", value: 0 },
					true
				);
				trackTikTokEvent("use_template", {
					template_id: id,
					invitation_id: data?.invitation_id
				});
				navigate(`/manageWeddingInvitation/${data?.invitation_id}`);
			});
		} else {
			navigate(`/login?redirect=/weddingInvitation/${id}`);
		}
	};

	const handleRSVP = () => {
		setShowCommentModal(true);
	};
	const handlePostComment = (shouldSubscribeToNotifications: boolean) => {
		// setShowCommentModal(false);
		const hasBeenOpened = localStorage.getItem("notificationSubModalOpened");
		if (shouldSubscribeToNotifications && !hasBeenOpened) {
			setShowNotificationSubModal(true);
		}
	};

	const handleUpdateEntrance = (entrance: INVITATION_ENTRANCE) => {
		setSelectedTemplateEntrance(entrance);
		setOpenEntrance(false);
	};
	const handleUpdateColor = (color: TemplateColorObject) => {
		setSelectedColorMatrix(color.id);
	};
	const handleCloseUpload = () => {
		setUpload(false);
	};
	const postWeddingDate = useMemo(() => {
		if (!selectedInvitation) return new Date();
		return addDays(new Date(selectedInvitation.wedding_date), 2);
	}, [selectedInvitation]);

	const shouldDisableUpload = useMemo(() => {
		if (!postWeddingDate) return false;
		return isAfter(new Date(), postWeddingDate);
	}, [postWeddingDate]);

	const getContent = () => {
		if (validationError && !isValidatingInvitation && !selectedInvitation) {
			const errorMessage =
				"data" in (validationError as IError)
					? (validationError as IError).data.message
					: (validationError as IError).error;
			const isLocked = (errorMessage || "").includes("password");
			if (isLocked) {
				return <LockedInvitation errorMessage={errorMessage || ""} />;
			}
			return <NoDataFallback message={errorMessage} />;
		}
		if (isValidatingInvitation || isFetchingInvitation || !selectedInvitation || !selectedTemplateId) {
			return <InvitationLoader />;
		}
		if (selectedInvitation.is_coming_soon) {
			return <NoDataFallback message={"Invitation is not published yet"} />;
		}

		return (
			<>
				<WeddingMeta
					title={invitation.title}
					description={invitation.description}
					image={invitation.image}
					url={invitation.url}
				/>
				{InvitationEntrance}
				{selectedInvitation.type === INVITATION_TYPES.TEMPLATE && (
					<Box className={"template-color-selector"}>
						<InvitationColorPicker
							templateId={selectedInvitation?.template_id || 1}
							template_color={1}
							onColorPick={handleUpdateColor}
						/>
					</Box>
				)}
				{selectedInvitation.type === INVITATION_TYPES.TEMPLATE && !openEntrance && (
					<Box className={"template-entrance-selector"}>
						<InvitationEntrancePicker
							selectedEntrance={selectedTemplateEntrance ?? selectedInvitation.entrance ?? INVITATION_ENTRANCE.NAMES}
							onEntrancePick={handleUpdateEntrance}
						/>
					</Box>
				)}
				<Box ref={invitationWrapperRef} className={`invitation-wrapper ${openEntrance ? "show" : "hide"}`}>
					{!isInvitationLoaded && (
						<Stack spacing={1} position={"absolute"} width={"inherit"}>
							<Skeleton variant={"rectangular"} height={"40vh"} />
							<Skeleton variant={"rectangular"} height={"20vh"} />
							<Skeleton variant={"rectangular"} height={"30vh"} />
						</Stack>
					)}
					{templates}
				</Box>
				{selectedInvitation?.type === INVITATION_TYPES.TEMPLATE && (
					<ButtonComponent
						className={`invitation-buy-button ${openEntrance ? "show" : "hide"}`}
						variant={"contained"}
						style={{
							background: InvitationColorMatrix[selectedTemplateId][selectedColorMatrix].addTemplateButton.background
						}}
						onClick={handleUseTemplate}
						disabled={isCreatingTemplate}
						text={"ADD TEMPLATE"}
						loading={isCreatingTemplate}
					/>
				)}
				{selectedInvitation?.status === INVITATION_STATUS.PUBLISHED && (
					<Box
						className={`invitation-control-bar ${selectedInvitation.language === LANG_AR ? RTL : ""} ${openEntrance ? "show" : "hide"}`}
					>
						<IconButton
							className={"location-menu-item"}
							onClick={handleRSVP}
							disabled={isCreatingTemplate}
							loading={isCreatingTemplate}
						>
							<Box
								className={"icon"}
								style={{
									background:
										InvitationColorMatrix[selectedTemplateId][selectedInvitation.template_color].addTemplateButton
											.background
								}}
							>
								<Favorite />
							</Box>
							<Typography> {myComment ? lang.UPDATE_RESPONSE : lang.RESPOND_NOW}</Typography>
						</IconButton>
						{selectedInvitation?.wedding_album && (
							<IconButton
								className={`location-menu-item ${!myComment || !myComment?.is_email_verified || shouldDisableUpload ? "disabled" : ""}`}
								onClick={() => {
									setUpload(true);
								}}
								disabled={!myComment || !myComment?.is_email_verified || shouldDisableUpload}
							>
								<Box className={"icon"}>
									<PhotoLibrary />
								</Box>
								<Typography>{lang.MEMORIES}</Typography>
							</IconButton>
						)}
						{!selectedInvitation?.fieldVisibility?.comments?.hidden && (
							<IconButton className={"location-menu-item"} onClick={handeScrollToComments}>
								<Box className={"icon"}>
									<Comment />
								</Box>
								<Typography>{lang.comments}</Typography>
							</IconButton>
						)}

						{!selectedInvitation?.fieldVisibility?.location?.hidden && (
							<IconButton className={"location-menu-item"} onClick={handeScrollToLocation}>
								<Box className={"icon"}>
									<LocationOn />
								</Box>
								<Typography>{lang.location}</Typography>
							</IconButton>
						)}
					</Box>
				)}

				{myComment && myComment.is_email_verified && (
					<Box className={`notification_bell_wrapper ${openEntrance ? "show" : "hide"}`}>
						<IconButton
							onClick={() => setShowNotificationSubModal(true)}
							style={{
								background:
									InvitationColorMatrix[selectedTemplateId][selectedInvitation.template_color].addTemplateButton
										.background
							}}
						>
							<Notifications />
						</IconButton>
					</Box>
				)}
				{showCommentModal && (
					<RsvpModal
						open={showCommentModal}
						canEdit={false}
						onClose={() => setShowCommentModal(false)}
						data={selectedInvitation as Invitations}
						password={password || ""}
						id={id || ""}
						postComment={handlePostComment}
						colorObject={InvitationColorMatrix[selectedTemplateId][selectedInvitation.template_color]}
					/>
				)}
				{upload && (
					<UploadMemories
						id={(selectedInvitation as Invitations)?._id}
						language={(selectedInvitation as Invitations)?.language}
						open={true}
						onClose={handleCloseUpload}
						email={myComment?.email || ""}
						password={password || ""}
					/>
				)}
				{showNotificationSubModal && (
					<NotificationsConfig
						onClose={() => {
							setShowNotificationSubModal(false);
							localStorage.setItem("notificationSubModalOpened", "true");
						}}
						myComment={myComment}
					/>
				)}
				{rsvpReminder && (
					<ConfirmModal
						open={true}
						title={`${selectedInvitation?.language === LANG_EN ? "RSVP Reminder" : "تأكيد الحضور"}`}
						message={`${selectedInvitation?.language === LANG_EN ? "Before you continue exploring our invitation, we would truly appreciate your response ✨" : "قبل أن تتابعوا استكشاف دعوتنا، نود أن تتفضلوا بالرد علينا."}`}
						onConfirm={() => {
							handleRSVP();
							setRSVPReminder(false);
						}}
						onClose={() => setRSVPReminder(false)}
						confirmText={`${selectedInvitation.language === LANG_EN ? "Yes, Respond" : "تأكيد"}`}
						cancelText={`${selectedInvitation.language === LANG_EN ? "No, Cancel" : "متابعه الإستكشاف"}`}
						loading={false}
					/>
				)}

				<ImageSlideShow
					open={showImageGallerySlides}
					selectedInvitationGallery={selectedInvitationGallery || []}
					initialSlideShowImage={initialSlideShowImage}
					handleCloseModal={() => {
						dispatch(toggleImageGallerySlides(0));
					}}
				/>
			</>
		);
	};

	return <WeddingInvitationContainer ref={containerRef}>{getContent()}</WeddingInvitationContainer>;
};

export default WeddingInvitationTemplates;
